import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { UsersApi } from '../../App/api/users.api'; // adjust path to your UsersApi

// ───────────────────────────────────────────────
// Async Thunks – for backend synchronization
// ───────────────────────────────────────────────

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const response = await UsersApi.adminAllNotification();
      // console.log("response.data:",response.data.data)
      return response.data?.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch notifications');
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      await UsersApi.markAdminNotificationRead(notificationId);
      return notificationId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to mark notification as read');
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      await UsersApi.markAllAdminNotificationsRead();
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to mark all notifications as read');
    }
  }
);

// ───────────────────────────────────────────────
// Slice
// ───────────────────────────────────────────────

const initialState = {
  notifications: [],           // newest first
  unreadCount: 0,
  status: 'idle',              // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  lastFetched: null,
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,

  reducers: {
    // For real-time socket additions
    notificationAdded: (state, action) => {
      const payload = action.payload;

      // Normalize fields
      const normalized = {
        ...payload,
        id: payload._id || payload.id,
        read: payload.is_read ?? payload.read ?? false,
        is_read: payload.is_read ?? payload.read ?? false,
      };

      // Prevent duplicates
      const exists = state.notifications.some(n => n.id === normalized.id);
      if (!exists) {
        state.notifications.unshift(normalized);
        if (!normalized.read) {
          state.unreadCount += 1;
        }
      }
    },

    // Optional: clear all (logout, etc.)
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.status = 'idle';
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    // fetchNotifications
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.lastFetched = new Date().toISOString();

        const raw = action.payload || [];

        state.notifications = raw
          .map((n) => ({
            ...n,
            id: n._id || n.id,
            read: n.is_read ?? n.read ?? false,
            is_read: n.is_read ?? n.read ?? false,
          }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // newest first

        state.unreadCount = state.notifications.filter((n) => !n.read).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      });

    // mark single as read
    builder
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const id = action.payload;
        const notif = state.notifications.find((n) => n.id === id);
        if (notif && !notif.read) {
          notif.read = true;
          notif.is_read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        // You could add rollback or error toast here via middleware
        console.warn('Mark as read failed:', action.payload);
      });

    // mark all as read
    builder
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.notifications = state.notifications.map((n) => ({
          ...n,
          read: true,
          is_read: true,
        }));
        state.unreadCount = 0;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        console.warn('Mark all read failed:', action.payload);
      });
  },
});

// ───────────────────────────────────────────────
// Exports
// ───────────────────────────────────────────────

export const {
  notificationAdded,
  clearNotifications,
} = notificationSlice.actions;

export default notificationSlice.reducer;